import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaShieldAlt, FaUser, FaUsers } from "react-icons/fa";
import { Spinner } from "src/components/shared/atoms/spinner";
import { useTheme } from "src/context/theme.context";
import {
  useGetUserBlocksQuery,
  useGetUserByIdQuery,
  useGetUsersByIdsQuery,
} from "../../services/user.Service";

const EmptyState = ({
  title,
  description,
  tone,
}: {
  title: string;
  description: string;
  tone: "amber" | "rose";
}) => (
  <div
    className={`rounded-3xl border border-dashed p-8 text-center ${
      tone === "amber"
        ? "border-amber-200 bg-amber-50/80 text-amber-900"
        : "border-rose-200 bg-rose-50/80 text-rose-900"
    }`}
  >
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 shadow-sm">
      <FaShieldAlt className={tone === "amber" ? "text-amber-500" : "text-rose-500"} />
    </div>
    <h3 className="text-lg font-semibold">{title}</h3>
    <p className="mt-2 text-sm opacity-80">{description}</p>
  </div>
);

const UserReferenceCard = ({
  userId,
  user,
  tone,
}: {
  userId: string;
  user?: { id: string; name: string; email?: string | null; pictureURL?: string | null };
  tone: "amber" | "rose";
}) => {
  const toneClasses =
    tone === "amber"
      ? "border-amber-200/80 bg-white/80 hover:border-amber-300 hover:bg-amber-50/80"
      : "border-rose-200/80 bg-white/80 hover:border-rose-300 hover:bg-rose-50/80";
  const iconClasses = tone === "amber" ? "from-amber-500 to-orange-600" : "from-rose-500 to-pink-600";
  const textClasses = tone === "amber" ? "text-amber-700" : "text-rose-700";

  if (!user) {
    return (
      <div className={`rounded-3xl border p-4 shadow-sm ${toneClasses}`}>
        <div className="flex items-center gap-4">
          <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${iconClasses} text-white shadow-lg`}>
            <FaUser />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">Unknown user</div>
            <div className="truncate font-mono text-xs text-gray-500">{userId}</div>
            <div className={`mt-1 text-xs font-medium ${textClasses}`}>User document was not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={`/users/${userId}`}
      className={`block rounded-3xl border p-4 shadow-sm transition-all duration-300 no-underline hover:-translate-y-1 hover:no-underline ${toneClasses}`}
    >
      <div className="flex items-center gap-4">
        {user.pictureURL ? (
          <img
            src={user.pictureURL}
            alt={user.name}
            className="h-12 w-12 rounded-2xl object-cover shadow-md"
          />
        ) : (
          <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${iconClasses} text-white shadow-lg`}>
            <FaUser />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-gray-900">{user.name || "Unnamed user"}</div>
          <div className="truncate text-xs text-gray-500">{user.email || "No email"}</div>
          <div className={`mt-1 truncate font-mono text-xs font-medium ${textClasses}`}>{userId}</div>
        </div>
        <div className={`text-xs font-semibold uppercase tracking-[0.2em] ${textClasses}`}>Open</div>
      </div>
    </Link>
  );
};

const UserBlocksPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { isDarkMode } = useTheme();

  const { data: user, isLoading: userLoading, isError: userError } = useGetUserByIdQuery(userId || "", {
    skip: !userId,
  });
  const {
    data: blocks,
    isLoading: blocksLoading,
    isFetching: blocksFetching,
    isError: blocksError,
  } = useGetUserBlocksQuery(userId || "", {
    skip: !userId,
  });

  const allRelatedIds = useMemo(() => {
    const ids = [...(blocks?.blockedUsersIds || []), ...(blocks?.blockedByUsersIds || [])];
    return Array.from(new Set(ids.filter(Boolean)));
  }, [blocks?.blockedByUsersIds, blocks?.blockedUsersIds]);

  const { data: usersMap = {}, isFetching: usersFetching } = useGetUsersByIdsQuery(allRelatedIds, {
    skip: allRelatedIds.length === 0,
  });

  if (!userId) {
    return <div className="p-6 text-red-600">User ID not found.</div>;
  }

  if (userLoading || blocksLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
          <h2 className="text-2xl font-bold text-red-800">User not found</h2>
          <p className="mt-3 text-red-600">We could not load this user details page.</p>
          <Link
            to="/users"
            className="mt-6 inline-flex rounded-2xl bg-red-600 px-5 py-3 font-semibold text-white no-underline transition-colors hover:bg-red-700 hover:no-underline"
          >
            Back to Users
          </Link>
        </div>
      </div>
    );
  }

  const blockedUsersIds = blocks?.blockedUsersIds || [];
  const blockedByUsersIds = blocks?.blockedByUsersIds || [];

  return (
    <div
      className={`min-h-screen py-8 ${
        isDarkMode
          ? "bg-gradient-to-br from-gray-950 via-slate-950 to-gray-900"
          : "bg-gradient-to-br from-orange-50 via-white to-rose-50"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4">
        <Link
          to={`/users/${userId}`}
          className="mb-6 inline-flex items-center gap-3 text-emerald-600 no-underline transition-colors hover:text-emerald-700 hover:no-underline"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
            <FaArrowLeft />
          </div>
          <span className="text-lg font-semibold">Back to User Details</span>
        </Link>

        <div className="rounded-[2rem] border border-white/70 bg-white/90 p-8 shadow-2xl backdrop-blur">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-white shadow-xl">
                <FaShieldAlt className="text-2xl" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900">User Blocks</h1>
              <p className="mt-3 max-w-2xl text-lg text-gray-600">
                Review who <span className="font-semibold text-gray-900">{user.name}</span> has blocked and who has blocked them.
              </p>
            </div>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-600">Blocked Users</div>
                  <div className="mt-3 text-4xl font-bold text-amber-900">{blockedUsersIds.length}</div>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500 text-white shadow-lg">
                  <FaUser />
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-rose-200 bg-gradient-to-br from-rose-50 to-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-rose-600">Blocked By</div>
                  <div className="mt-3 text-4xl font-bold text-rose-900">{blockedByUsersIds.length}</div>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-500 text-white shadow-lg">
                  <FaUsers />
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Total Links</div>
                  <div className="mt-3 text-4xl font-bold text-slate-900">
                    {blockedUsersIds.length + blockedByUsersIds.length}
                  </div>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-700 text-white shadow-lg">
                  <FaShieldAlt />
                </div>
              </div>
            </div>
          </div>

          {blocksError && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              We could not load the `user_blocks` document cleanly, so empty lists are shown as a safe fallback.
            </div>
          )}

          <div className="mt-10 grid gap-8 xl:grid-cols-2">
            <section className="rounded-[2rem] border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-6 shadow-lg">
              <div className="mb-6 flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg">
                  <FaUser />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Blocked by This User</h2>
                  <p className="text-sm text-gray-600">Accounts that this user has chosen to block.</p>
                </div>
              </div>

              <div className="space-y-4">
                {blockedUsersIds.length === 0 ? (
                  <EmptyState
                    title="No blocked users"
                    description="This user has not blocked anyone yet."
                    tone="amber"
                  />
                ) : (
                  blockedUsersIds.map((id) => (
                    <UserReferenceCard
                      key={`blocked-${id}`}
                      userId={id}
                      user={usersMap[id]}
                      tone="amber"
                    />
                  ))
                )}
              </div>
            </section>

            <section className="rounded-[2rem] border border-rose-200/80 bg-gradient-to-br from-rose-50 via-white to-pink-50 p-6 shadow-lg">
              <div className="mb-6 flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-lg">
                  <FaUsers />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Blocked This User</h2>
                  <p className="text-sm text-gray-600">Accounts that currently block this user.</p>
                </div>
              </div>

              <div className="space-y-4">
                {blockedByUsersIds.length === 0 ? (
                  <EmptyState
                    title="No users blocking them"
                    description="No blocking relationships were found against this user."
                    tone="rose"
                  />
                ) : (
                  blockedByUsersIds.map((id) => (
                    <UserReferenceCard
                      key={`blocked-by-${id}`}
                      userId={id}
                      user={usersMap[id]}
                      tone="rose"
                    />
                  ))
                )}
              </div>
            </section>
          </div>

          {(blocksFetching || usersFetching) && (
            <div className="mt-6 text-sm font-medium text-gray-500">Refreshing block relationships…</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserBlocksPage;
